import { supabase } from './supabaseClient.js';

const normalizeHeaders = (input) => {
  if (!input) {
    return {};
  }

  if (input instanceof Headers) {
    return Object.fromEntries(input.entries());
  }

  return { ...input };
};

const fetchSession = async (forceRefresh = false) => {
  const response = forceRefresh
    ? await supabase.auth.refreshSession()
    : await supabase.auth.getSession();

  if (response.error) {
    throw response.error;
  }

  return response.data?.session || response.data || null;
};

export const getAuthenticatedSession = async ({ forceRefresh = false } = {}) => {
  try {
    return await fetchSession(forceRefresh);
  } catch (error) {
    console.warn('[authenticatedApiClient] getAuthenticatedSession failed', error);
    return null;
  }
};

export const buildAuthenticatedRequestHeaders = async (headers, sessionOverride = null) => {
  const session = sessionOverride || (await getAuthenticatedSession());
  const normalizedHeaders = normalizeHeaders(headers);

  if (!session?.access_token) {
    return normalizedHeaders;
  }

  return {
    ...normalizedHeaders,
    Authorization: `Bearer ${session.access_token}`,
  };
};

export const authenticatedFetch = async (input, init = {}) => {
  let session = await getAuthenticatedSession();
  const headers = await buildAuthenticatedRequestHeaders(init.headers, session);

  let response = await fetch(input, {
    ...init,
    headers,
  });

  if (response.status !== 401) {
    return response;
  }

  session = await getAuthenticatedSession({ forceRefresh: true });
  if (!session?.access_token) {
    return response;
  }

  const retryHeaders = await buildAuthenticatedRequestHeaders(init.headers, session);
  return fetch(input, {
    ...init,
    headers: retryHeaders,
  });
};

export default authenticatedFetch;
